import React, { useEffect, useState } from "react";

const Url = process.env.REACT_APP_URl;

interface Photo {
  urlafter: string;
  urlbefor: string;
}

interface History {
  _id: string;
  startDate: string;
  endDate: string;
  status: string;
  operationName: string;
  operationType: string;
  numberOfPhotos: number;
  photos: Photo[];
  createdAt: string;
}

const HistoryPopup: React.FC = () => {

  const [history, setHistory] = useState<History[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await fetch(Url + '/history', {
        headers : {   
          "Authorization": "Bearer " + localStorage.getItem("usertoken"),
          "Content-Type": "application/json"
        }
      });
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const data = await res.json();
      setHistory(data.histories);
      
    } catch (err: any) {
      setError("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = () => {
    setShowModal(true);
    if (history.length === 0) {
      fetchHistory();
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError("");
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      const options: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'short' 
      };
      return date.toLocaleDateString('en-US', options);
    }
  };

  const handleDownload = async (imageUrl: string, fileName: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  const styles = {
    historyButton: {
      // padding: '8px 16px',
      border: '2px solid #746AFF',
      backgroundColor: 'transparent',
      color: '#746AFF',
      // borderRadius: '6px',
      // fontWeight: '500',
      // cursor: 'pointer',
      transition: 'all 0.2s ease',
      // fontSize: '14px'
    },
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column' as const,
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
    },
    header: {
      padding: '20px 24px 16px',
      borderBottom: '1px solid #e9ecef',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: '#f8f9fa'
    },
    title: {
      margin: 0,
      fontSize: '20px',
      fontWeight: '600',
      color: '#333'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      color: '#666',
      cursor: 'pointer',
      padding: '0',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      transition: 'background-color 0.2s ease'
    },
    body: {
      flex: 1,
      overflowY: 'auto' as const,
      padding: 0
    },
    loader: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '3rem',
      flexDirection: 'column' as const
    },
    spinner: {
      width: '40px',
      height: '40px',
      border: '3px solid #f3f3f3',
      borderTop: '3px solid #746AFF',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    errorMessage: {
      padding: '1rem 1.5rem',
      backgroundColor: '#f8d7da',
      color: '#721c24',
      borderRadius: '4px',
      margin: '1rem'
    },
    emptyMessage: {
      padding: '3rem 1.5rem',
      textAlign: 'center' as const,
      color: '#666',
      fontSize: '16px'
    },
    historyItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px 24px',
      borderBottom: '1px solid #f0f0f0',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    },
    thumbnail: {
      width: '60px',
      height: '60px',
      borderRadius: '12px',
      objectFit: 'cover' as const,
      marginRight: '16px',
      backgroundColor: '#f8f9fa'
    },
    content: {
      flex: 1,
      minWidth: 0
    },
    itemTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#333',
      margin: '0 0 4px 0'
    },
    subtitle: {
      fontSize: '14px',
      color: '#666',
      margin: 0
    },
    date: {
      fontSize: '14px',
      color: '#746AFF',
      fontWeight: '500',
      marginLeft: 'auto',
      flexShrink: 0
    },
    downloadButton: {
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      border: 'none',
      backgroundColor: '#746AFF',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: '12px',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <button
        style={styles.historyButton}
        onClick={handleShowModal}
        className="button-reset padd-15"
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#746AFF';
          e.currentTarget.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#746AFF';
        }}
      >
        History
      </button>

      {showModal && (
        <div style={styles.overlay} onClick={handleCloseModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.header}>
              <h5 style={styles.title}>History</h5>
              <button
                style={styles.closeButton}
                onClick={handleCloseModal}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>
            
            <div style={styles.body}>
              {loading ? (
                <div style={styles.loader}>
                  <div style={styles.spinner}></div>
                  <p style={{ marginTop: '1rem', color: '#666' }}>Loading history...</p>
                </div>
              ) : error ? (
                <div style={styles.errorMessage}>
                  {error}
                </div>
              ) : history.length === 0 ? (
                <div style={styles.emptyMessage}>
                  No history available
                </div>
              ) : (
                history.map((item) =>
                  item.photos.map((photo, photoIndex) => (
                    <div
                      key={`${item._id}-${photoIndex}`}
                      style={styles.historyItem}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <img
                        src={photo.urlafter}
                        alt={`${item.operationName} - Photo ${photoIndex + 1}`}
                        style={styles.thumbnail}
                        onError={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.alt = 'Image not available';
                        }}
                      />
                      
                      <div style={styles.content}>
                        <div style={styles.itemTitle}>
                          {item.operationName}
                        </div>
                        <div style={styles.subtitle}>
                          {item.operationType}
                        </div>
                      </div>
                      
                      <div style={styles.date}>
                        {formatDate(item.createdAt)}
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(
                            photo.urlafter, 
                            `${item.operationName}_after_${photoIndex + 1}.jpg`
                          );
                        }}
                        style={styles.downloadButton}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4a2eb8'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#746AFF'}
                        title="Download image"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path 
                            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                          <path 
                            d="M7 10L12 15L17 10" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                          <path 
                            d="M12 15V3" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  ))
                )
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HistoryPopup;